import CircularProgress from "@mui/material/CircularProgress";
import axios from "axios";
import Cookies from "js-cookie";
import { Briefcase, MapPinIcon, CheckCircle, Calendar } from "lucide-react";
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";

import { useAlert } from "../../constants/AlertProvider";

export default function WorkExp() {
  const { candidateId: routeCandidateId } = useParams();
  const [userData, setUserData] = useState({
    name: "",
    email: "",
    phone_number: "",
    address: "",
  });
  const [applicationId, setApplicationId] = useState(null);
  const [experiences, setExperiences] = useState([]);
  const [isRecruiterView, setIsRecruiterView] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const showAlert = useAlert();

  const formatDate = (dateStr) => {
    if (!dateStr) return "Present";
    const [year, month, day] = dateStr.split("-");
    const date = new Date(`${year}-${month}-${day}`);
    return date.toLocaleString("en-US", { month: "long", year: "numeric" });
  };

  useEffect(() => {
    const fetchExperiences = async () => {
      try {
        setIsLoading(true);
        const loggedInUserId = Cookies.get("id");
        const targetUserId = routeCandidateId || loggedInUserId;

        if (!targetUserId) {
          throw new Error("Target User ID is missing");
        }

        if (routeCandidateId) {
          setIsRecruiterView(true);
        }

        let userRes;
        if (isRecruiterView) {
          userRes = await axios.get(`/api/candidate-profile/${targetUserId}/`, {
            withCredentials: true,
          });
        } else {
          userRes = await axios.get(`/api/users/${targetUserId}/`, {
            withCredentials: true,
          });
        }
        const userProfileData = userRes.data;
        setUserData(userProfileData);

        if (
          userProfileData.applicationids &&
          userProfileData.applicationids.length > 0
        ) {
          const appId = userRes.data.applicationids[0].id;
          setApplicationId(appId);

          const expResponse = await axios.get(
            `/api/experience/?application_id=${appId}`,
            {
              headers: { "X-CSRFToken": Cookies.get("csrftoken") },
              withCredentials: true,
            },
          );

          if (expResponse.data && Array.isArray(expResponse.data.results)) {
            setExperiences(expResponse.data.results);
          } else {
            throw new Error("Invalid API response format");
          }
        } else {
          setExperiences([]);
        }
      } catch (error) {
        console.error("Error fetching experiences:", error);
        showAlert("Failed to fetch work experience.", "error");
      } finally {
        setIsLoading(false);
      }
    };

    fetchExperiences();
  }, [showAlert, routeCandidateId, isRecruiterView]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <CircularProgress color="primary" size={30} />
      </div>
    );
  }
  return (
    <div className="space-y-6">
      {experiences.length === 0 ? (
        <div className="text-center py-12 rounded-lg">
          <Briefcase className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
            No Work Experience Added
          </h3>
          {!isRecruiterView && (
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Your work experience will be displayed here. You can add it by
              editing your profile.
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Work Experience ({experiences.length})
            </h2>
          </div>

          {experiences.map((exp) => {
            const locationString = [exp.city, exp.state, exp.country]
              .filter(Boolean)
              .join(", ");
            return (
              <div
                key={exp.id}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4"
              >
                <div className="flex items-start space-x-4">
                  <Briefcase className="h-6 w-6 text-gray-400 dark:text-gray-500 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                      {exp.title} at {exp.company}
                    </h3>
                    <div className="mt-1 flex items-center gap-x-4 text-sm text-gray-500 dark:text-gray-400">
                      <span className="flex items-center">
                        <Calendar className="h-4 w-4 mr-1.5" />
                        {formatDate(exp.start_date)} -{" "}
                        {formatDate(exp.end_date)}
                      </span>
                      {locationString && (
                        <span className="flex items-center">
                          <MapPinIcon className="h-4 w-4 mr-1.5" />
                          {locationString}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="mt-3 flex flex-col">
                  <label
                    className="text-sm font-bold text-gray-600 dark:text-gray-400"
                    htmlFor="responsibilities"
                  >
                    Responsibilities:
                  </label>
                  <p
                    className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap"
                    id="responsibilities"
                  >
                    {exp.responsibilities}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
